import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import Auth0RoleDetailsContext from '@totem/components/services/remoteAccess/auth0role/details/Auth0RoleDetailsContext';
import { IBAuth0RoleDetail } from '@totem/components/services/remoteAccess/auth0role/details/types';
import { Auth0UserItem } from '@totem/components/services/remoteAccess/auth0users/details/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_ROLES_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  roleId: string;
  children?: ReactNode;
};

const Auth0RoleDetailsContainer = ({ roleId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [role, setRole] = useState<IBAuth0RoleDetail | null>(null);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<Auth0UserItem[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  useEffect(() => {
    if (!refreshData || !roleId) {
      return;
    }

    setRefreshData(false);

    setLoading(true);

    fetch(`${AUTH0_ROLES_ENDPOINT}/${roleId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: IBAuth0RoleDetail) => {
        setRole(result ?? null);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load role details.' });
      })
      .finally(() => setLoading(false));

    setUsersLoading(true);

    fetch(`${AUTH0_ROLES_ENDPOINT}/${roleId}/users`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: Auth0UserItem[]) => {
        setUsers(result ?? []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load role users.' });
      })
      .finally(() => setUsersLoading(false));
  }, [refreshData, roleId, getApiToken]);

  const onRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  return (
    <Auth0RoleDetailsContext.Provider
      value={{ loading, role, users, usersLoading, onRefresh }}
    >
      {children}
    </Auth0RoleDetailsContext.Provider>
  );
};

export default Auth0RoleDetailsContainer;
